import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { toObjectMap } from "@@/utilities/dictionary";
import { tagCase } from "@@/utilities/tag-case";
import { z } from "zod";

/**
 * Format tag; corresponding to param-case (kebab-casing) the tag
 * @param {string} tag
 * @returns {string}
 */
const tagFormat = <T extends string = string>(tag: T): T => tagCase<T>(tag);

/**
 * Takes an array of tags (or undefined) and returns an array with formatted tags or an empty array
 * @param {(string[] | undefined)} tags
 * @returns {string[]}
 */
const tagsFormat = (tags: string[] | undefined): string[] =>
    (tags || []).map(tagFormat);

const deliveryTags = [
    "PICKUP",
    "TABLE_SERVICE",
    "HOME_DELIVERY",
    "HOME_DELIVERY_ROUTE",
    "HOME_DELIVERY_SMALL",
    "HOME_DELIVERY_MEDIUM",
    "HOME_DELIVERY_LARGE",
    "HOME_DELIVERY_XL",
    "HOME_DELIVERY_MEGA_BOUTA_XXXXXLARGE",
] as const;
type DeliveryTag = (typeof deliveryTags)[number];
const deliveryTagMap = toObjectMap<DeliveryTag>([...deliveryTags]);

type Tags = {
    readonly _type: "TAGS";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly areas: string[];
    readonly categories: string[];
    readonly custom: string[];
    readonly delivery: string[];
};
const tagsZodSchema: z.ZodSchema<Tags> = z.object({
    _type: z.literal("TAGS"),
    _createdAt: isoAndUnixZodSchema,
    areas: z.string().array(),
    categories: z.string().array(),
    custom: z.string().array(),
    delivery: z.string().array(),
});

const tagsFactory = (params?: {
    areas?: string[];
    categories?: string[];
    custom?: string[];
    delivery?: string[];
}): Tags => {
    return {
        _type: "TAGS",
        _createdAt: nowIsoAndUnix(),
        areas: params?.areas?.map(tagFormat) || [],
        custom: params?.custom?.map(tagFormat) || [],
        categories: params?.categories?.map(tagFormat) || [],
        delivery:
            params?.delivery?.map(value => tagFormat<string>(value)) || [],
    };
};

const negateTag = (tag: string) =>
    tag.toLowerCase().startsWith("NO_") ? tag : `NO_${tag}`;

export {
    deliveryTagMap,
    deliveryTags,
    negateTag,
    tagFormat,
    tagsFactory,
    tagsFormat,
    tagsZodSchema,
};
export type { DeliveryTag, Tags };
